import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Typography
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageContainer } from '../components/AdminContentWrapper';
import UserProfileService from '../../../services/UserProfileService';
import SemanticModelService from '../../../services/SemanticModelService';
import SemanticDatabaseService from '../../../services/SemanticDatabaseService';
import SemanticWorkspaceService from '../../../services/SemanticWorkspaceService';
import {
  SemanticModelSection,
  SemanticModelFormDialog,
  SemanticModelViewDialog,
  SemanticModelDeleteDialog
} from '../../../components/semantic-model';

const resolveUserId = (profile) => profile?.user_id || profile?.userId || profile?.user_uuid;
const resolveAccountId = (profile) => profile?.account_id || profile?.accountUuid || profile?.account_uuid;

const asModelList = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.semantic_models)) {
    return response.semantic_models;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

const asModelRecord = (response) => {
  if (!response) {
    return null;
  }
  if (response.semantic_model_id || response.semanticModelId) {
    return response;
  }
  if (response.data) {
    return asModelRecord(response.data);
  }
  if (response.item) {
    return asModelRecord(response.item);
  }
  return null;
};

const asDatabaseList = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.items)) {
    return response.items;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

// fetchWithAuth lanza "HTTP <status>: <cuerpo JSON>": se muestra su `message`.
const readableError = (error, fallback) => {
  const match = (error?.message || '').match(/^HTTP (\d+): (.*)$/s);
  if (!match) {
    return error?.message || fallback;
  }
  if (match[1] === '403') {
    return 'No tienes permiso para realizar esta acción.';
  }
  try {
    return JSON.parse(match[2])?.message || fallback;
  } catch {
    return fallback;
  }
};

// "HTTP 409: {message, workspaces}" del backend al eliminar un modelo en uso.
const workspacesFromConflict = (error) => {
  const match = (error?.message || '').match(/^HTTP 409: (.*)$/s);
  if (!match) {
    return null;
  }
  try {
    const parsed = JSON.parse(match[1]);
    return Array.isArray(parsed?.workspaces) ? parsed.workspaces : null;
  } catch {
    return null;
  }
};

const SemanticModels = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profile, setProfile] = useState(null);
  const [records, setRecords] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Espacios de trabajo que usan el modelo a eliminar: si hay alguno no se
  // permite eliminarlo (el backend lo vuelve a comprobar con un 409).
  const [deleteUsage, setDeleteUsage] = useState({ checking: false, workspaces: [], error: '' });
  const [isRefreshingDatabases, setIsRefreshingDatabases] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Selector "Vista: grafo" sin un modelo elegido: diálogo para elegir cuál.
  const [graphPickerOpen, setGraphPickerOpen] = useState(false);

  // View state management with URL parameters
  const currentView = searchParams.get('view') || 'list';
  const selectedModelId = searchParams.get('modelId') || null;

  const accountId = resolveAccountId(profile);
  const userAccountId = resolveAccountId(profile);

  const databaseIndex = useMemo(() => {
    const index = {};
    (databases || []).forEach((database) => {
      const id =
        database?.semantic_database_id ||
        database?.semanticDatabaseId ||
        database?.id ||
        database?.ID;
      if (id) {
        index[id] = database;
      }
    });
    return index;
  }, [databases]);

  const activeRecords = useMemo(() => {
    return (records || []).filter((item) => !item?.soft_delete?.deleted);
  }, [records]);

  const deletedRecords = useMemo(() => {
    return (records || []).filter((item) => item?.soft_delete?.deleted);
  }, [records]);

  const hydrateRecord = useCallback(async (record) => {
    if (!record?.semantic_model_id) {
      return record;
    }
    try {
      const detailResponse = await SemanticModelService.getById(record.semantic_model_id);
      return asModelRecord(detailResponse) || record;
    } catch (detailError) {
      console.error('Error obteniendo detalle del modelo semántico:', detailError);
      return record;
    }
  }, []);

  const refreshRecords = useCallback(async () => {
    try {
      const modelsResponse = await SemanticModelService.list({ includeDeleted: true });
      setRecords(asModelList(modelsResponse));
    } catch (refreshError) {
      console.error('Error actualizando modelos semánticos:', refreshError);
      setSnackbar({
        open: true,
        message: 'No se pudo actualizar el listado de modelos semánticos.',
        severity: 'error'
      });
    }
  }, []);

  const refreshDatabasesList = useCallback(async () => {
    if (!accountId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar la cuenta actual para recargar las bases semánticas.',
        severity: 'error'
      });
      return;
    }
    try {
      setIsRefreshingDatabases(true);
      const databasesResponse = await SemanticDatabaseService.list(accountId, { includeDeleted: false });
      setDatabases(asDatabaseList(databasesResponse));
    } catch (refreshError) {
      console.error('Error actualizando bases semánticas:', refreshError);
      setSnackbar({
        open: true,
        message: 'No fue posible actualizar el listado de bases semánticas.',
        severity: 'error'
      });
    } finally {
      setIsRefreshingDatabases(false);
    }
  }, [accountId]);

  const loadInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const profileData = await UserProfileService.getProfile();
      if (!profileData) {
        throw new Error('No se encontró el perfil del usuario.');
      }
      setProfile(profileData);

      const resolvedAccountId = resolveAccountId(profileData);
      const resolvedUserId = resolveUserId(profileData);

      if (!resolvedUserId) {
        console.warn('No pudimos determinar el usuario autenticado.');
      }
      if (!resolvedAccountId) {
        throw new Error('No pudimos determinar la cuenta asociada.');
      }

      let databasesResponse = [];
      try {
        databasesResponse = await SemanticDatabaseService.list(resolvedAccountId, { includeDeleted: false });
      } catch (databaseError) {
        console.error('Error cargando bases semánticas:', databaseError);
        setSnackbar({
          open: true,
          message: 'No fue posible cargar las bases semánticas. Algunas opciones podrían no mostrarse.',
          severity: 'warning'
        });
        databasesResponse = [];
      }

      const modelsResponse = await SemanticModelService.list({ includeDeleted: true });

      setDatabases(asDatabaseList(databasesResponse));
      setRecords(asModelList(modelsResponse));
    } catch (loadError) {
      console.error('Error cargando modelos semánticos:', loadError);
      setError(loadError.message || 'No se pudieron cargar los modelos semánticos.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Handle context restoration from URL parameters
  useEffect(() => {
    if (selectedModelId && currentView === 'list') {
      // If we have a selected model ID in the URL, highlight it in the list
      // This preserves context when returning from graph view
      console.log('Restoring context for model:', selectedModelId);
      
      // Find the model in the records and scroll to it if needed
      const modelRecord = records.find(record => 
        record.semantic_model_id === selectedModelId || record.id === selectedModelId
      );
      
      if (modelRecord) {
        // You could add visual highlighting or scrolling here
        console.log('Found model record for context restoration:', modelRecord);
      }
    }
  }, [selectedModelId, currentView, records]);

  const handleOpenCreate = () => {
    setFormMode('create');
    setSelectedRecord(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = async (record) => {
    setFormMode('edit');
    setSelectedRecord(record);
    setIsFormOpen(true);
    try {
      const hydrated = await hydrateRecord(record);
      if (hydrated) {
        setSelectedRecord(hydrated);
      }
    } catch (hydrateError) {
      console.error('Error preparando datos para edición del modelo:', hydrateError);
      setSnackbar({
        open: true,
        message: 'No se pudieron cargar todos los datos del modelo.',
        severity: 'warning'
      });
    }
  };

  const handleCloseForm = () => {
    if (isSaving) {
      return;
    }
    setIsFormOpen(false);
    setSelectedRecord(null);
  };

  const handleSubmitForm = async (formPayload) => {
    if (!userAccountId) {
      setSnackbar({
        open: true,
        message: 'No pudimos identificar la cuenta asociada para guardar el modelo.',
        severity: 'error'
      });
      return;
    }

    setIsSaving(true);
    try {
      if (formMode === 'edit' && selectedRecord?.semantic_model_id) {
        const updateResponse = await SemanticModelService.update(selectedRecord.semantic_model_id, formPayload);
        const updatedRecord = asModelRecord(updateResponse) || {
          ...selectedRecord,
          ...formPayload
        };

        setRecords((prev) =>
          prev.map((item) =>
            item.semantic_model_id === selectedRecord.semantic_model_id
              ? { ...item, ...updatedRecord }
              : item
          )
        );

        setSnackbar({ open: true, message: 'Modelo actualizado correctamente.', severity: 'success' });
      } else {
        const createResponse = await SemanticModelService.create(formPayload);
        const createdRecord = asModelRecord(createResponse);

        if (createdRecord) {
          setRecords((prev) => [...prev, createdRecord]);
        } else {
          await refreshRecords();
        }

        setSnackbar({ open: true, message: 'Modelo creado correctamente.', severity: 'success' });
      }

      setIsFormOpen(false);
      setSelectedRecord(null);
    } catch (submitError) {
      console.error('Error guardando modelo semántico:', submitError);
      setSnackbar({
        open: true,
        message: readableError(submitError, 'Ocurrió un error al guardar el modelo.'),
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenView = async (record) => {
    setViewRecord(record);
    try {
      const hydrated = await hydrateRecord(record);
      if (hydrated) {
        setViewRecord(hydrated);
      }
    } catch (hydrateError) {
      console.error('Error obteniendo detalle del modelo:', hydrateError);
    }
  };

  const handleCloseView = () => {
    setViewRecord(null);
  };

  const findWorkspacesUsingModel = useCallback(async (semanticModelId) => {
    const found = [];
    let nextToken;
    do {
      // eslint-disable-next-line no-await-in-loop
      const page = await SemanticWorkspaceService.listByAccount(accountId, { nextToken });
      (page?.items || []).forEach((workspace) => {
        if (!workspace?.soft_delete?.deleted && (workspace?.models_linked || []).includes(semanticModelId)) {
          found.push({ semantic_workspace_id: workspace.semantic_workspace_id, name: workspace.name });
        }
      });
      nextToken = page?.next_cursor || null;
    } while (nextToken);
    return found;
  }, [accountId]);

  const handleOpenDelete = async (record) => {
    setDeleteRecord(record);
    setDeleteUsage({ checking: true, workspaces: [], error: '' });
    try {
      const workspaces = await findWorkspacesUsingModel(record.semantic_model_id);
      setDeleteUsage({ checking: false, workspaces, error: '' });
    } catch (usageError) {
      console.error('Error verificando el uso del modelo:', usageError);
      setDeleteUsage({
        checking: false,
        workspaces: [],
        error: 'No se pudo verificar si el modelo está en uso. Al confirmar se volverá a comprobar.'
      });
    }
  };

  const handleCloseDelete = () => {
    if (isDeleting) {
      return;
    }
    setDeleteRecord(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRecord?.semantic_model_id) {
      return;
    }

    setIsDeleting(true);
    try {
      const deleteResponse = await SemanticModelService.softDelete(deleteRecord.semantic_model_id);
      const deletedRecordResponse = asModelRecord(deleteResponse);

      if (deletedRecordResponse) {
        setRecords((prev) =>
          prev.map((item) =>
            item.semantic_model_id === deletedRecordResponse.semantic_model_id
              ? { ...item, ...deletedRecordResponse }
              : item
          )
        );
      } else {
        await refreshRecords();
      }

      setSnackbar({
        open: true,
        message: 'Se realizó el borrado lógico del modelo.',
        severity: 'success'
      });
      setDeleteRecord(null);
    } catch (deleteError) {
      const conflictWorkspaces = workspacesFromConflict(deleteError);
      if (conflictWorkspaces) {
        // Se enlazó a un espacio de trabajo después de abrir el diálogo:
        // se queda abierto mostrando cuáles.
        setDeleteUsage({ checking: false, workspaces: conflictWorkspaces, error: '' });
        return;
      }
      console.error('Error realizando soft delete del modelo:', deleteError);
      setSnackbar({
        open: true,
        message: readableError(deleteError, 'No se pudo eliminar el modelo.'),
        severity: 'error'
      });
      setDeleteRecord(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleOpenGraphView = (record) => {
    if (!record?.semantic_model_id) {
      setSnackbar({
        open: true,
        message: 'No se pudo identificar el modelo semántico para mostrar el grafo.',
        severity: 'error'
      });
      return;
    }

    // Navigate to graph view with model ID
    navigate(`/admin/home/semantic-models/modelos/${record.semantic_model_id}/graph`);
  };

  const openGraph = (semanticModelId) => {
    navigate(`/admin/home/semantic-models/modelos/${semanticModelId}/graph`);
  };

  const handleViewChange = (newView) => {
    if (newView === 'graph') {
      // El grafo es de UN modelo: el seleccionado; si no hay ninguno y solo
      // existe uno, ese; si hay varios, se pregunta cuál (antes solo
      // cambiaba ?view=graph y la página no hacía nada).
      if (selectedModelId) {
        openGraph(selectedModelId);
      } else if (activeRecords.length === 1) {
        openGraph(activeRecords[0].semantic_model_id);
      } else if (activeRecords.length > 1) {
        setGraphPickerOpen(true);
      } else {
        setSnackbar({ open: true, message: 'No hay modelos semánticos para mostrar en el grafo.', severity: 'info' });
      }
    } else if (newView === 'list') {
      // If switching to list view, update URL parameters
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('view', 'list');
      setSearchParams(newSearchParams);
    } else {
      // Update view in URL parameters
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('view', newView);
      setSearchParams(newSearchParams);
    }
  };

  const handleModelSelect = (record) => {
    // Update selected model in URL parameters for context preservation
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('modelId', record.semantic_model_id);
    setSearchParams(newSearchParams);
  };

  return (
    <PageContainer
      title="Modelos semánticos"
      description="Administra los modelos construidos sobre tus bases semánticas y controla su disponibilidad."
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <SemanticModelSection
        activeRecords={activeRecords}
        deletedRecords={deletedRecords}
        filter={filter}
        onFilterChange={setFilter}
        onCreate={handleOpenCreate}
        currentView={currentView}
        onViewChange={handleViewChange}
        onView={handleOpenView}
        onEdit={handleOpenEdit}
        onDelete={handleOpenDelete}
        onGraphView={handleOpenGraphView}
        onSelect={handleModelSelect}
        selectedModelId={selectedModelId}
        isLoading={isLoading}
        error={null}
        databaseIndex={databaseIndex}
      />

      <SemanticModelFormDialog
        open={isFormOpen}
        mode={formMode}
        initialValues={selectedRecord}
        databases={databases}
        onClose={handleCloseForm}
        onSubmit={handleSubmitForm}
        isSaving={isSaving}
        onRefreshDatabases={accountId ? refreshDatabasesList : undefined}
        isRefreshingDatabases={isRefreshingDatabases}
        userAccountId={userAccountId}
        accountId={accountId}
      />

      <SemanticModelViewDialog
        open={Boolean(viewRecord)}
        onClose={handleCloseView}
        record={viewRecord}
        database={
          viewRecord?.semantid_database_id
            ? databaseIndex[viewRecord.semantid_database_id]
            : null
        }
        accountName={profile?.company?.name}
        accountId={accountId}
      />

      <SemanticModelDeleteDialog
        open={Boolean(deleteRecord)}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        record={deleteRecord}
        isDeleting={isDeleting}
        isCheckingUsage={deleteUsage.checking}
        usedByWorkspaces={deleteUsage.workspaces}
        usageError={deleteUsage.error}
      />

      <Dialog open={graphPickerOpen} onClose={() => setGraphPickerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>¿Qué modelo quieres ver en el grafo?</DialogTitle>
        <DialogContent dividers>
          <List dense disablePadding>
            {activeRecords.map((record) => (
              <ListItemButton
                key={record.semantic_model_id}
                onClick={() => {
                  setGraphPickerOpen(false);
                  openGraph(record.semantic_model_id);
                }}
              >
                <ListItemText
                  primary={record.name}
                  secondary={`${Array.isArray(record.contained_tables_id) ? record.contained_tables_id.length : 0} tablas`}
                />
              </ListItemButton>
            ))}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            También puedes abrirlo con el ícono de grafo de cada modelo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGraphPickerOpen(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default SemanticModels;
